import React, { useEffect, useRef, useState, useMemo } from 'react';

// =============================================================================
// CONFIGURATION
// =============================================================================
const CONFIG = {
  // Card dimensions - responsive sizes
  cardSizes: {
    mobile: { width: 120, height: 173 },   // < 640px
    tablet: { width: 150, height: 216 },   // 640px - 1024px  
    desktop: { width: 180, height: 259 },  // > 1024px
  },
  cardGap: 16, // ~1-2cm gap between cards
  
  // Animation speed (pixels per second)
  speed: 40,
  
  // Customer photo dimensions - responsive
  customerSizes: {
    mobile: { width: 160, height: 232 },
    tablet: { width: 200, height: 290 },
    desktop: { width: 220, height: 318 },
  },
  
  customerPhoto: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193729.webp",
  
  items: [
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193128.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193803.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193655.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214194300.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193449.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214194127.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193211.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193836.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193254.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193909.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193320.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193943.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193346.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214194024.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193414.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214194054.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193518.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214194158.webp" },
    { dress: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214193615.webp", output: "https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260214194226.webp" },
  ],
};

// Get responsive sizes based on screen width
const getResponsiveSizes = (stageWidth) => {
  if (stageWidth < 640) {
    return { card: CONFIG.cardSizes.mobile, customer: CONFIG.customerSizes.mobile };
  } else if (stageWidth < 1024) {
    return { card: CONFIG.cardSizes.tablet, customer: CONFIG.customerSizes.tablet };
  }
  return { card: CONFIG.cardSizes.desktop, customer: CONFIG.customerSizes.desktop };
};

// Preload images
const preloadImages = (urls) => Promise.all(
  urls.map(url => new Promise(resolve => {
    const img = new Image();
    img.onload = img.onerror = () => resolve();
    img.src = url;
  }))
);

// =============================================================================
// CONVEYOR ITEM - A single card in the conveyor
// =============================================================================
const ConveyorItem = ({ src, x, opacity, cardWidth, cardHeight, zIndex = 10 }) => (
  <div
    className="absolute rounded-xl overflow-hidden shadow-xl ring-1 ring-slate-200 bg-white"
    style={{
      top: '50%',
      transform: 'translateY(-50%)',
      left: x,
      width: cardWidth,
      height: cardHeight,
      opacity: opacity,
      zIndex: zIndex,
      willChange: 'left, opacity',
    }}
  >
    <img src={src} alt="" className="w-full h-full object-contain bg-slate-50" loading="eager" />
  </div>
);

// =============================================================================
// MAIN COMPONENT - Conveyor Belt Animation
// =============================================================================
const HomeHeroVisualDemo = () => {
  const stageRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [stageWidth, setStageWidth] = useState(0);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const offsetRef = useRef(0);
  const [offset, setOffset] = useState(0);

  // Get responsive sizes
  const sizes = useMemo(() => getResponsiveSizes(stageWidth), [stageWidth]);

  // Calculate layout values
  const layout = useMemo(() => {
    if (stageWidth === 0) return null;
    
    const { card, customer } = sizes;
    const cardWidth = card.width;
    const customerWidth = customer.width;
    const itemSpacing = cardWidth + CONFIG.cardGap;
    const totalItems = CONFIG.items.length;
    const totalConveyorLength = totalItems * itemSpacing;
    
    // Customer photo boundaries
    const customerLeft = stageWidth / 2 - customerWidth / 2;
    const customerRight = stageWidth / 2 + customerWidth / 2;
    const customerCenter = stageWidth / 2;
    
    // Dress conveyor: from left edge to customer left edge
    // Output conveyor: starts from behind customer photo (at center) and emerges to the right
    const dressEndX = customerLeft - 20; // Fade zone starts 20px before customer
    const outputStartX = customerCenter; // Output starts at center (behind customer photo)
    const outputVisibleX = customerRight; // Output becomes visible after passing customer's right edge
    
    return {
      cardWidth,
      cardHeight: card.height,
      customerWidth: customer.width,
      customerHeight: customer.height,
      itemSpacing,
      totalConveyorLength,
      customerLeft,
      customerRight,
      customerCenter,
      dressEndX,
      outputStartX,
      outputVisibleX,
    };
  }, [stageWidth, sizes]);

  // Preload images
  useEffect(() => {
    const urls = [CONFIG.customerPhoto, ...CONFIG.items.flatMap(i => [i.dress, i.output])];
    preloadImages(urls).then(() => setImagesLoaded(true));
  }, []);

  // Measure stage
  useEffect(() => {
    if (!stageRef.current) return;
    const measure = () => setStageWidth(stageRef.current?.offsetWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [imagesLoaded]);

  // Animation loop
  useEffect(() => {
    if (!imagesLoaded || !layout) return;

    const animate = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;
      
      // Update offset (pixels moved)
      offsetRef.current += (CONFIG.speed * deltaTime) / 1000;
      
      // Loop the offset when it exceeds total conveyor length
      if (offsetRef.current >= layout.totalConveyorLength) {
        offsetRef.current = offsetRef.current % layout.totalConveyorLength;
      }
      
      setOffset(offsetRef.current);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [imagesLoaded, layout]);

  // Calculate visible items for both conveyors
  const { dressItems, outputItems } = useMemo(() => {
    if (!layout) return { dressItems: [], outputItems: [] };

    const { itemSpacing, totalConveyorLength, dressEndX, outputStartX, cardWidth, customerLeft } = layout;
    const items = CONFIG.items;
    const totalItems = items.length;
    
    const dressItems = [];
    const outputItems = [];
    
    // Calculate the delay for output conveyor
    // Output should start when dress reaches customer's left edge
    // Dress starts from -cardWidth, travels to customerLeft
    // Adding itemSpacing to bring output one slot earlier (so it appears as dress is merging, not after)
    const dressJourneyDistance = customerLeft + cardWidth - itemSpacing;
    
    for (let i = 0; i < totalItems; i++) {
      // Calculate base position for this item in the conveyor
      const basePos = i * itemSpacing;
      
      // ===================
      // DRESS CONVEYOR (Left side, moving RIGHT toward center)
      // ===================
      // Dress starts from far left and moves toward customer photo
      // Dress stops rendering at customer's left edge (doesn't go through to right side)
      let dressX = basePos - totalConveyorLength + offset;
      
      // Wrap around for continuous loop
      while (dressX < -itemSpacing) {
        dressX += totalConveyorLength;
      }
      while (dressX > customerLeft) {
        dressX -= totalConveyorLength;
      }
      
      // Only render dress if it hasn't reached customer's left edge yet
      // This prevents dress from appearing on the right side of customer photo
      if (dressX > -cardWidth - 20 && dressX < customerLeft) {
        dressItems.push({
          key: `dress-${i}`,
          src: items[i].dress,
          x: dressX,
          opacity: 1, // Full opacity - no fading
          zIndex: 10, // Lower than customer photo so it goes behind
        });
      }
      
      // ===================
      // OUTPUT CONVEYOR (Right side, moving RIGHT away from center)
      // ===================
      // Output starts from behind customer photo (at center) and emerges to the right
      // The customer photo (z-index 20) covers the output until it slides past
      const outputOffset = offset - dressJourneyDistance;
      let outputX = outputStartX + basePos - totalConveyorLength + outputOffset;
      
      // Wrap around for continuous loop
      while (outputX < outputStartX) {
        outputX += totalConveyorLength;
      }
      while (outputX > stageWidth + cardWidth) {
        outputX -= totalConveyorLength;
      }
      
      // Render output from behind customer photo (outputStartX = center)
      // The output will be hidden by customer photo until it emerges past customerRight
      if (outputX >= outputStartX && outputX < stageWidth + 20) {
        outputItems.push({
          key: `output-${i}`,
          src: items[i].output,
          x: outputX,
          opacity: 1, // Full opacity - customer photo acts as natural mask
          zIndex: 10, // Lower than customer photo (z-20) so it appears from behind
        });
      }
    }
    
    return { dressItems, outputItems };
  }, [layout, offset, stageWidth]);

  if (!imagesLoaded) {
    return (
      <section className="relative w-full bg-white overflow-hidden" data-testid="hero-visual-demo">
        <div className="pt-20 sm:pt-24 pb-3 px-4">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight text-center max-w-4xl mx-auto">
            Sell More. Return Less. Everywhere.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-500 leading-relaxed text-center max-w-2xl mx-auto">
            Turn browsers into confident buyers with interactive shopping experiences across online, in-store, and social.
          </p>
        </div>
        <div className="flex justify-center pb-6 px-4">
          <a
            href="https://nanokart.ai/5f0f23fa-9f83-44ef-b73c-e2ecb15c2326/customers/tryon"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base rounded-xl transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 hover:scale-[1.02]"
            data-testid="try-live-demo-btn"
          >
            Try Live Demo
          </a>
        </div>
        <div className="w-full h-[400px] flex items-center justify-center">
          <div className="w-10 h-10 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full bg-white overflow-hidden" data-testid="hero-visual-demo">
      {/* Headline */}
      <div className="pt-20 sm:pt-24 pb-3 sm:pb-4 px-4">
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight text-center max-w-4xl mx-auto">
          Sell More. Return Less. Everywhere.
        </h1>
        <p className="mt-3 text-base sm:text-lg text-slate-500 leading-relaxed text-center max-w-2xl mx-auto">
          Turn browsers into confident buyers with interactive shopping experiences across online, in-store, and social.
        </p>
      </div>

      {/* Hero CTA */}
      <div className="flex justify-center pb-6 sm:pb-7 px-4">
        <a
          href="https://nanokart.ai/5f0f23fa-9f83-44ef-b73c-e2ecb15c2326/customers/tryon"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-7 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base rounded-xl transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 hover:scale-[1.02]"
          data-testid="try-live-demo-btn"
        >
          Try Live Demo
        </a>
      </div>
      
      {/* Animation Stage */}
      <div 
        ref={stageRef}
        className="relative w-full h-[400px] sm:h-[440px] lg:h-[480px] overflow-hidden"
      >
        {stageWidth > 0 && layout && (
          <>
            {/* Dress Conveyor - Left side (goes behind customer photo) */}
            {dressItems.map(item => (
              <ConveyorItem
                key={item.key}
                src={item.src}
                x={item.x}
                opacity={item.opacity}
                cardWidth={layout.cardWidth}
                cardHeight={layout.cardHeight}
                zIndex={item.zIndex}
              />
            ))}
            
            {/* Customer Photo - Fixed Center */}
            <div 
              className="absolute rounded-xl overflow-hidden shadow-2xl ring-2 ring-white/80 z-20"
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: layout.customerWidth,
                height: layout.customerHeight,
              }}
            >
              <img src={CONFIG.customerPhoto} alt="" className="w-full h-full object-contain bg-slate-50" />
            </div>
            
            {/* Output Conveyor - Right side (emerges from behind customer photo) */}
            {outputItems.map(item => (
              <ConveyorItem
                key={item.key}
                src={item.src}
                x={item.x}
                opacity={item.opacity}
                cardWidth={layout.cardWidth}
                cardHeight={layout.cardHeight}
                zIndex={item.zIndex}
              />
            ))}
          </>
        )}
      </div>
    </section>
  );
};

// Hero Text Section
export const HeroTextSection = () => (
  <section className="py-6 sm:py-10 bg-white" data-testid="hero-text-section">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-base sm:text-lg text-blue-600 font-medium">
          See it. Try it. Buy it.
        </p>
      </div>
    </div>
  </section>
);

export { HomeHeroVisualDemo };
export default HomeHeroVisualDemo;
